import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AdminDebitCreditPage.css";

const EXPORT_BATCH = 500;
const EXCEL_SHEET_TITLE = "Admin Debit Credit";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

const formatDateDisplay = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const matchesSearch = (t, q) => {
  if (!q) return true;
  const userId = String(t.user_id ?? "").toLowerCase();
  const uuid = String(t.uuid ?? "").toLowerCase();
  const email = String(t.email ?? t.emailId ?? "").toLowerCase();
  const name = String(t.name ?? "").toLowerCase();
  const currency = String(t.short_name ?? t.currency ?? "").toLowerCase();
  const txType = String(t.transaction_type ?? "").toLowerCase();
  return (
    userId.includes(q) ||
    uuid.includes(q) ||
    email.includes(q) ||
    name.includes(q) ||
    currency.includes(q) ||
    txType.includes(q)
  );
};

const rowToExport = (row) => ({
  "Created At": row.createdAt ?? "",
  "Created At (Local)": formatDate(row.createdAt),
  "User ID": row.user_id ?? "",
  UUID: row.uuid ?? "",
  Name: row.name ?? "",
  Email: row.email ?? row.emailId ?? "",
  Amount: row.amount ?? "",
  Currency: row.short_name ?? row.currency ?? "",
  Description: row.description ?? "",
  Type: row.transaction_type ?? "",
  "From Admin": row.from_admin ?? "",
  "Record ID": row._id ?? "",
});

const AdminDebitCreditPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [search, setSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const res = await AdminService.getAdminDebitCreditTransactions({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (!res?.success) {
        setTransactions([]);
        setFetchError(res?.message || "Failed to load admin debit/credit transactions");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      setTransactions(list);
      const totalCount = Number(res.totalCount ?? list.length) || 0;
      const totalPages = Math.max(
        1,
        Number(res.totalPages) || (pagination.limit > 0 ? Math.ceil(totalCount / pagination.limit) : 1)
      );
      const currentPage = Number(res.currentPage ?? pagination.page) || 1;
      setPagination((p) => ({
        ...p,
        totalCount,
        totalPages,
        page: currentPage,
      }));
    } catch {
      setTransactions([]);
      setFetchError("Failed to load admin debit/credit transactions");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((t) => matchesSearch(t, q));
  }, [transactions, search]);

  const counts = useMemo(() => {
    const credit = transactions.filter((t) =>
      String(t.transaction_type ?? "").toUpperCase().includes("CREDIT")
    ).length;
    const debit = transactions.filter((t) =>
      String(t.transaction_type ?? "").toUpperCase().includes("DEBIT")
    ).length;
    return { credit, debit };
  }, [transactions]);

  const goToPage = (next) => {
    if (next < 1 || next > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page: next }));
  };

  const fetchAllForExport = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getAdminDebitCreditTransactions({
        page: currentPage,
        limit: EXPORT_BATCH,
      });
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = Math.max(
        1,
        Number(res?.totalPages) ||
          (Number(res?.totalCount) > 0
            ? Math.ceil(Number(res.totalCount) / EXPORT_BATCH)
            : 1)
      );
      currentPage += 1;
    } while (currentPage <= lastPage);

    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((t) => matchesSearch(t, q));
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllForExport();
      if (!allRows.length) {
        setToast({ type: "error", message: "No transactions match to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `admin_debit_credit_${dateStamp}.xlsx`);

      setToast({
        type: "success",
        message: `Exported ${allRows.length} transaction(s) to Excel.`,
      });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({
        type: "error",
        message: e?.message || "Failed to export Excel file.",
      });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Admin Debit Credit</span>
          </p>
          <h1 className="dashboard-hero__title">Admin Debit Credit</h1>
          <p className="dashboard-hero__subtitle">
            Admin debit and credit transactions
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-exchange-alt" />
        </div>
      </section>

      {toast && (
        <div className={`admintrans-toast admintrans-toast--${toast.type}`} role="status">
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="admintrans-section">
          <div className="admintrans-section__header">
            <div className="admintrans-section__title-wrap">
              <span className="admintrans-section__icon">
                <i className="fas fa-wallet" />
              </span>
              <h2 className="admintrans-section__title">Admin Debit / Credit Transactions</h2>
            </div>
            <div className="admintrans-section__stats admintrans-section__stats--actions">
              <span className="admintrans-stat">
                <strong>{pagination.totalCount}</strong> Transactions
              </span>
              <span className="admintrans-stat">
                <strong>{counts.credit}</strong> Credit
              </span>
              <span className="admintrans-stat">
                <strong>{counts.debit}</strong> Debit
              </span>
              <button
                type="button"
                className="admintrans-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download report as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>
          {fetchError && (
            <p className="admintrans-empty" role="alert">{fetchError}</p>
          )}
          <div className="admintrans-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by user ID, UUID, email, name, currency or type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admintrans-search-input"
            />
          </div>
          <div className="admintrans-table-wrap">
            <table className="admintrans-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Created</th>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Amount</th>
                  <th>Currency</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>From Admin</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="admintrans-empty">
                      <i className="fas fa-spinner fa-spin" /> Loading...
                    </td>
                  </tr>
                ) : (
                  filtered.map((row, idx) => {
                    const typeRaw = String(row.transaction_type ?? "");
                    const typeClass = typeRaw.toUpperCase().includes("CREDIT")
                      ? "credit"
                      : typeRaw.toUpperCase().includes("DEBIT")
                        ? "debit"
                        : "neutral";
                    return (
                      <tr key={row._id ?? `${row.user_id ?? "u"}-${row.createdAt ?? idx}-${idx}`}>
                        <td>{idx + 1}</td>
                        <td>{formatDateDisplay(row.createdAt)}</td>
                        <td>
                          <span className="admintrans-mono">{row.uuid ?? row.user_id}</span>
                        </td>
                        <td>{row.email ?? row.emailId ?? "—"}</td>
                        <td>
                          {typeof row.amount === "number"
                            ? row.amount.toLocaleString("en-IN", { maximumFractionDigits: 4 })
                            : row.amount}
                        </td>
                        <td>
                          <strong>{row.short_name ?? row.currency ?? "—"}</strong>
                        </td>
                        <td>{row.description ?? "—"}</td>
                        <td>
                          <span className={`admintrans-type admintrans-type--${typeClass}`}>
                            {row.transaction_type ?? "—"}
                          </span>
                        </td>
                        <td>
                          <span className="admintrans-admin">{row.from_admin || "—"}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && filtered.length === 0 && (
            <p className="admintrans-empty">No transactions match your search.</p>
          )}
          {!loading && pagination.totalPages > 1 && (
            <div
              className="admintrans-search-wrap"
              style={{ justifyContent: "center", gap: "1rem", borderTop: "1px solid var(--border)" }}
            >
              <button
                type="button"
                className="admintrans-type"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Prev
              </button>
              <span className="admintrans-stat">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                type="button"
                className="admintrans-type"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default AdminDebitCreditPage;
